// src/components/PieChart.js
import { useRef } from "react";
import { Chart as Chartjs, registerables } from 'chart.js';
import { Scatter, Chart } from "react-chartjs-2";
import PropTypes from "prop-types";
import moment from "moment";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";

import { event_types } from "../DeviceEvents/event_types";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;

function getLabelTimes(tooltipItems) {
    var index = tooltipItems[0].dataIndex;
    var next_index = index + 1 == tooltipItems[0].dataset.data.length ? index : index + 1;

    var first = moment(tooltipItems[0].dataset.data[index].x).format("LLL");
    var last = moment(tooltipItems[0].dataset.data[next_index].x).format("LLL");

    return { first, last };
}

function getTooltipTotalCount(tooltipItems) {
    var totalCount = 0;

    for (var item of tooltipItems) {
        totalCount += item.raw.data.count;
    }

    return totalCount;
}

function ScatterChart({ chartData = [], advancedToggle, advanced_labels, startTime, endTime, groupSize, setStartTime, setEndTime }) {

    const chartRef = useRef(null);

    const scrollEvent = (chart) => {
        // console.log(timeout);
        current_chart = chart;
        if (typeof timeout != 'undefined') {
            clearTimeout(timeout);
            timeout = undefined;
        }

        timeout = setTimeout(() => {
            runWheelScrollEvent(current_chart);
            timeout = undefined;
        }, 200);
    }

    const runWheelScrollEvent = ({ chart }) => {
        var start_time, end_time, group_size;
        let min_limit = moment(0)
        let max_limit = moment();
        // console.log(chart.scales.x.min)
        let min_moment = moment(chart.scales.x.min);
        let max_moment = moment(chart.scales.x.max);

        if (min_moment.isBefore(min_limit)) {
            start_time = min_limit;
        }
        else {
            start_time = min_moment;
        }

        if (max_moment.isAfter(max_limit)) {
            end_time = max_limit;
        }
        else {
            end_time = max_moment;
        }

        setStartTime(start_time);
        setEndTime(end_time);

    };

    var options = {
        layout: {
            padding: 8
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        plugins: {
            zoom: {
                zoom: {
                    mode: 'x',
                    drag: {
                        enabled: true,
                    },
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true,
                    },
                    speed: 0.5, sensitivity: 0.5,
                    onZoomComplete: scrollEvent,
                },
                limits: {
                    x: {
                        min: 1725112800 * 1000, // 1st September 2024
                        max: moment().unix() * 1000,
                        minRange: 16000 * 1000,
                    }
                },
                legend: {
                    // position: 'top',
                    display: false
                },
                title: {
                    display: false,
                    text: 'Event chart'
                },
                tooltip: {
                    enabled: true,
                }
            },
            elements: {
                point: {
                    radius: 4,
                    hoverRadius: 5
                }
            },
            tooltip: {
                borderWidth: 3,
                callbacks: {
                    title: (tooltipItems) => {
                        return tooltipItems[0].dataset.label;
                    },
                    afterTitle: (tooltipItems) => {
                        var event = advanced_labels.filter((e) => e.text == tooltipItems[0].dataset.label);
                        if (event[0].level < 2) {
                            return event[0].desc;
                        }
                    },
                    beforeBody: () => { return ' ' },
                    label: () => { return false },
                    afterBody: (tooltipItems) => {
                        var totalCount = getTooltipTotalCount(tooltipItems);

                        for (var item of tooltipItems) {
                            totalCount += item.raw.data.count;
                        }

                        return 'Total Count: ' + totalCount;
                    },
                    beforeFooter: () => { return ' ' },
                    footer: (tooltipItems) => {
                        var { first, last } = getLabelTimes(tooltipItems);
                        var totalCount = getTooltipTotalCount(tooltipItems);

                        if (first == last || totalCount == 1) {
                            return first;
                        }
                        return 'From: ' + first;
                    },
                    afterFooter: (tooltipItems) => {
                        var { first, last } = getLabelTimes(tooltipItems);
                        var totalCount = getTooltipTotalCount(tooltipItems);

                        if (first == last || totalCount == 1) {
                            return false;
                        }
                        return 'To: ' + last;
                    }
                },
                borderColor: (tooltipItem) => {
                    return tooltipItem.tooltip.labelColors[0].borderColor;
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                min: startTime,
                max: endTime
            },
            y: {
                type: 'linear',
                position: 'left',
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    min: 0,
                    max: advanced_labels.length,
                    callback: (context, index) => {
                        var arr = advanced_labels;
                        if (index < arr.length) {
                            return arr[index].text;
                        }
                    }
                }
            }
        }
    };

    var scatterChart = (
        <Scatter
            ref={chartRef}
            data={chartData.data}
            options={options}
        />
    );

    if (scatterChart.ref.current) {
        for (var scale in scatterChart.ref.current.scales) {
            if (scale.substring(0, 1) == 'y') {
                for (var index in scatterChart.ref.current.scales[scale].ticks) {
                    scatterChart.ref.current.scales[scale].ticks[index].value = index;
                }
            }
        }
    }

    return scatterChart;
};

// Typechecking props for the ScatterChart
ScatterChart.propTypes = {
    chartData: PropTypes.object,
    advancedToggle: PropTypes.bool,
    advanced_labels: PropTypes.array,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
};


export default ScatterChart;